import { useEffect, useRef } from 'react';
import { Award, Settings, Leaf, ShieldCheck } from 'lucide-react';

const values = [
  {
    id: 1,
    icon: Award,
    title: 'Excelencia Técnica y Calidad Constructiva',
    description: 'Aplicamos rigor en nuestra expertis en Ingeniería y Arquitectura para exceder estándares de calidad y durabilidad, desde cimentaciones hasta los acabados más finos.',
  },
  {
    id: 2,
    icon: Settings,
    title: 'Gestión Integral y Eficiencia Operativa',
    description: 'Nuestra capacidad para administrar y supervisar proyectos de forma integral asegura una optimización de tiempos y recursos, un control presupuestal y una minimización de riesgos, garantizando la satisfacción de nuestros socios y clientes.',
  },
  {
    id: 3,
    icon: Leaf,
    title: 'Sostenibilidad y Responsabilidad Social',
    description: 'Integramos prácticas de construcción sostenible y consideramos el impacto ambiental que tienen nuestros proyectos; edificando espacios funcionales que contribuyen positivamente al entorno y nuestra comunidad.',
  },
  {
    id: 4,
    icon: ShieldCheck,
    title: 'Integridad y Transparencia',
    description: 'Actuamos con máxima honestidad y ética en todas las interacciones con nuestros socios y clientes, así como fomentamos una comunicación abierta con clientes y colaboradores, construyendo relaciones de confianza duraderas.',
  },
];

export const AboutSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="nosotros"
      ref={sectionRef}
      data-testid="about-section"
      className="py-20 md:py-32 bg-[#F8FAFC] bg-pattern"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
          {/* Image */}
          <div className="lg:col-span-5 reveal-left">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_desarrollo-faraci/artifacts/jqkow9n2_trabajadores.jpg"
                alt="Equipo de construcción Faraci"
                data-testid="about-image"
                className="w-full h-[500px] object-cover"
              />
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#92D050]/20 -z-10" />
              <div className="absolute -top-6 -left-6 w-32 h-32 border-4 border-[#BF9000] -z-10" />
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-7 reveal-right">
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" />
              <span className="font-manrope uppercase tracking-widest text-xs font-bold text-[#BF9000]">
                Quiénes somos
              </span>
            </div>
            <h2
              data-testid="about-title"
              className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-[#385723] uppercase tracking-tight mb-6"
            >
              Pasión y Precisión
              <span className="text-[#92D050]"> en cada proyecto</span>
            </h2>
            <p className="font-manrope text-slate-600 text-base sm:text-lg leading-relaxed mb-6">
              En Faraci Edificaciones nos dedicamos con pasión y precisión a la concepción 
              y materialización de proyectos de construcción. Nuestra experiencia alcanza 
              desde inmuebles comerciales e industriales hasta viviendas y obras de 
              urbanización.
            </p>
            <p className="font-manrope text-slate-600 text-base sm:text-lg leading-relaxed mb-8">
              Desde 2019 hemos cimentado nuestra trayectoria en la innovación constructiva 
              y la excelencia técnica, utilizando tecnologías como drones y software BIM 
              para maximizar eficiencia, precisión y transparencia.
            </p>

            {/* Mission Box */}
            <div className="bg-[#385723] p-6 text-white">
              <h3 className="font-oswald text-lg uppercase tracking-wider mb-2 text-[#92D050]">
                Nuestra Misión
              </h3>
              <p className="font-manrope text-slate-200 leading-relaxed">
                Construir un futuro más sólido, impulsando la economía y desarrollando 
                espacios y proyectos dignos que contribuyan a un desarrollo comunitario.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="reveal">
          <div className="text-center mb-12">
            <h3
              data-testid="values-title"
              className="font-oswald text-2xl sm:text-3xl font-bold text-[#385723] uppercase tracking-tight"
            >
              Nuestros Distintivos Como Constructora
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={value.id}
                  data-testid={`value-card-${value.id}`}
                  className="value-card bg-white p-6 reveal"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#BF9000]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[#BF9000]" />
                    </div>
                    <div>
                      <h4 className="font-oswald text-lg font-bold text-[#385723] uppercase tracking-tight mb-2">
                        {value.title}
                      </h4>
                      <p className="font-manrope text-slate-600 text-sm leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
